import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Flex, Group} from '@mantine/core';
import type {Dispatch, SetStateAction} from 'react';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {UPDATE_FLOWCHART_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionMethod,
  ANALYTICS_MODULE,
  CloseType,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import notifications from 'notifications';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {
  buildExecutionFlowchartSpecFromDraftState,
  ExecutionFlowchartSpecVariant,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/buildExecutionFlowchartSpecFromDraftState';
import ExecutionFlowchartPane from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPane';
import type {ExecutionFlowchartReadinessSnapshot} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPaneTypes';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import ModalFooter from 'ui-library/modals/ModalFooter';
import {isServerRequestError} from 'util/Net';

import type {ProcessUpdateFlowchartModalProps} from './ProcessUpdateFlowchartModal';

const useFlowchartUpdateModalStyles = createStyles((theme) => ({
  footerSection: {
    flexShrink: 0,
    margin: '0 !important',
    paddingTop: theme.spacing.md,
    paddingRight: theme.spacing.md,
    paddingBottom: theme.spacing.md,
  },
  rootColumn: {
    flex: 1,
    minHeight: 0,
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  innerColumn: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
}));

export type ImprovedProcessUpdateFlowchartModalProps = Omit<
  ProcessUpdateFlowchartModalProps,
  'onRequestClose' | 'onRequestSubmit'
> & {
  /**
   * Called when the user dismisses the editor without saving (Cancel button). The parent is
   * responsible for closing the modal and discarding any unsaved draft edits so the underlying
   * execution viewer reverts to the pre-edit state.
   */
  onCancel: () => void;
  /**
   * Called after the draft has been persisted successfully. The parent should close the modal;
   * no draft revert is needed because the local draft already matches what was persisted.
   */
  onSubmit: () => void;
  draftState: FlowchartConfigWithMetadata | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  readinessSnapshot: ExecutionFlowchartReadinessSnapshot;
};

/**
 * Update-flowchart modal layout: footer + {@link ExecutionFlowchartPane} (PCM mode) with draft
 * and readiness supplied by the parent modal container.
 *
 * On Submit, converts the in-memory draft to a network {@link ExecutionProcessFlowchartSpec} and
 * persists it via {@link ProcessManagementDAO.updateProcess}, then notifies the caller and closes.
 */
export default function ImprovedProcessUpdateFlowchartModal(
  props: ImprovedProcessUpdateFlowchartModalProps
): JSX.Element {
  const {classes} = useFlowchartUpdateModalStyles();
  const {draftState, setDraftState, onCancel, onSubmit, readinessSnapshot} = props;
  const [hasBlockingErrors, setHasBlockingErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCancel = (): void => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      event_group_id: props.eventGroupId,
      action_origin: props.actionOrigin,
      action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
      close_type: CloseType.CANCEL,
      modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
    });
    onCancel();
  };

  const loadedFlowchartData = readinessSnapshot.data;

  const handleSubmit = async (): Promise<void> => {
    if (draftState == null || loadedFlowchartData == null) {
      notifications.error('Flowchart is still loading. Please wait and try again.');
      return;
    }
    setIsSubmitting(true);
    try {
      const flowchartSpec = buildExecutionFlowchartSpecFromDraftState({
        draftState,
        flowchartClientId: loadedFlowchartData.flowchartConfigId,
        persistedSchemaMapperConfigs: loadedFlowchartData.persistedSchemaMapperConfigs,
        specVariant: ExecutionFlowchartSpecVariant.PCM_UPDATE,
      });

      await ProcessManagementDAO.updateProcess({
        id: props.flowchartProcessId,
        flowchartSpec,
        sourceType: props.sourceType,
        actionOrigin: props.actionOrigin,
        actionMethod: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
        eventGroupId: props.eventGroupId,
      });
    } catch (error) {
      if (isServerRequestError(error)) {
        notifications.notifyError(error);
        return;
      }
      notifications.error('Could not save flowchart. Please try again or contact support if this persists.');
      throw error;
    } finally {
      setIsSubmitting(false);
    }

    props.onFlowchartModified();
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      event_group_id: props.eventGroupId,
      action_origin: props.actionOrigin,
      action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
      modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
    });
    onSubmit();
  };

  const isSubmitDisabled =
    draftState == null || loadedFlowchartData == null || hasBlockingErrors || isSubmitting;

  return (
    <Flex direction="column" className={classes.rootColumn}>
      <Flex direction="column" className={classes.innerColumn}>
        <ExecutionFlowchartPane
          editorMode={FlowchartEditorMode.PCM_EDITOR}
          processId={props.flowchartProcessId}
          draftState={draftState}
          setDraftState={setDraftState}
          readinessSnapshot={readinessSnapshot}
          onBlockingErrorsChange={setHasBlockingErrors}
        />
      </Flex>
      <ModalFooter sticky={false} className={classes.footerSection}>
        <Group position="right">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
            {...getTestProps({
              component: 'ImprovedProcessUpdateFlowchartModal',
              element: 'cancelButton',
            })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            loading={isSubmitting}
            {...getTestProps({
              component: 'ImprovedProcessUpdateFlowchartModal',
              element: 'submitButton',
            })}
          >
            Submit
          </Button>
        </Group>
      </ModalFooter>
    </Flex>
  );
}
